import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { toast } from "sonner";

function AuthShell({ children, title, subtitle }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#0f172a] text-white p-12">
        <div className="flex items-center gap-2.5">
          <BrandMark size={36} />
          <span className="font-display font-semibold text-lg">EPD Nigeria</span>
        </div>
        <div>
          <h1 className="font-display text-4xl font-semibold leading-tight">From plant data to Environmental Product Declaration.</h1>
          <p className="mt-5 text-slate-300 max-w-md">Enterprise EPD infrastructure for cement and ready-mix concrete manufacturers. Collect, calculate, check, generate and publish technically defensible EPDs.</p>
          <div className="mt-8 flex gap-2 text-xs text-slate-400 font-mono">
            <span>ISO 14025</span><span>·</span><span>ISO 21930</span><span>·</span><span>EN 15804+A2</span>
          </div>
        </div>
        <div className="text-xs text-slate-500">Restrained, auditable, institutional-grade.</div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-sm">
          <h2 className="font-display text-2xl font-semibold">{title}</h2>
          {subtitle && <p className="text-sm text-muted-foreground mt-1.5">{subtitle}</p>}
          <div className="mt-7">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role === "org_admin" && !u.org_active) nav("/awaiting-activation");
      else nav(u.needs_onboarding ? "/onboarding" : (u.role === "super_admin" ? "/app/admin" : "/app"));
    } catch (e2) { setErr(apiError(e2, "Invalid email or password")); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell title="Sign in" subtitle="Access your organisation's EPD workspace.">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="email">Work email</Label>
          <Input id="email" type="email" data-testid="login-email" value={email}
            onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" data-testid="login-password" value={password}
            onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" />
        </div>
        {err && <p data-testid="login-error" className="text-sm text-destructive">{err}</p>}
        <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full">
          {loading ? "Signing in…" : "Sign in"} <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
        <div className="text-sm text-center space-y-2">
          <Link to="/forgot-password" data-testid="forgot-link" className="text-primary hover:underline">Forgot password?</Link>
          <div className="pt-3 border-t text-slate-500">
            New to EPD Nigeria?{" "}
            <Link to="/register" data-testid="login-register-link" className="text-primary hover:underline font-medium">Create your organisation account →</Link>
          </div>
          <div className="text-xs text-slate-400">Need help? <a href="mailto:info@epd-nigeria.com" className="hover:underline">Contact EPD Nigeria</a></div>
        </div>
      </form>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/forgot-password", { email }); } catch (e2) {}
    setSent(true);
  };
  return (
    <AuthShell title="Reset password" subtitle="We'll email you a secure reset link.">
      {sent ? (
        <div className="space-y-4">
          <p data-testid="forgot-confirm" className="text-sm">If that email is registered, a reset link has been sent. The link expires in 1 hour.</p>
          <Link to="/login" className="text-primary hover:underline text-sm">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email">Work email</Label>
            <Input id="email" type="email" data-testid="forgot-email" value={email}
              onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" />
          </div>
          <Button type="submit" data-testid="forgot-submit" className="w-full">Send reset link</Button>
          <div className="text-sm text-center"><Link to="/login" className="text-primary hover:underline">Back to sign in</Link></div>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const nav = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (e2) { setErr(apiError(e2)); }
  };
  return (
    <AuthShell title="Set a new password">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="pw">New password</Label>
          <Input id="pw" type="password" data-testid="reset-password" value={password}
            onChange={(e) => setPassword(e.target.value)} required minLength={8} className="mt-1.5" />
          <p className="text-xs text-muted-foreground mt-1">At least 8 characters.</p>
        </div>
        {err && <p data-testid="reset-error" className="text-sm text-destructive">{err}</p>}
        <Button type="submit" data-testid="reset-submit" className="w-full">Update password</Button>
        <div className="text-sm text-center"><Link to="/login" className="text-primary hover:underline">Back to sign in</Link></div>
      </form>
    </AuthShell>
  );
}

export function AcceptInvite() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/auth/invite/${token}`).then(({ data }) => { setInfo(data); setName(data.name || ""); })
      .catch((e) => setErr(apiError(e))).finally(() => setLoaded(true));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await api.post("/auth/accept-invite", { token, password, name });
      setUser(data);
      nav("/app");
    } catch (e2) { setErr(apiError(e2)); }
  };

  return (
    <AuthShell title="Accept your invitation"
      subtitle={info ? `${info.organization} · ${info.role_label}` : "Join your organisation on EPD Nigeria."}>
      {!loaded ? <p className="text-sm text-muted-foreground">Loading…</p> :
        !info ? <p data-testid="invite-error" className="text-sm text-destructive">{err || "This invitation is invalid or already used."}</p> :
          <form onSubmit={submit} className="space-y-4">
            <div><Label>Email</Label><Input value={info.email} disabled className="mt-1.5" /></div>
            <div><Label htmlFor="nm">Full name</Label><Input id="nm" value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name" className="mt-1.5" /></div>
            <div><Label htmlFor="pw">Set password</Label><Input id="pw" type="password" data-testid="invite-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="mt-1.5" /></div>
            {err && <p className="text-sm text-destructive">{err}</p>}
            <Button type="submit" data-testid="invite-submit" className="w-full">Accept &amp; continue</Button>
          </form>}
    </AuthShell>
  );
}
